// src/app/[subdomain]/affiliate/page.tsx
"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import {
  LayoutDashboard,
  ShoppingBag,
  Link2,
  ClipboardList,
  DollarSign,
  Wallet,
  Megaphone,
  UserCheck,
  Bell,
  Copy,
  Check,
  LogOut,
  ArrowRight,
  Sparkles,
  Award,
  TrendingUp,
  MousePointerClick,
  Users,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Loader2,
  Search,
  ExternalLink,
  Plus,
  Send,
  ShieldCheck,
  KeyRound,
  Store,
} from "lucide-react";

type PortalTab =
  | "overview"
  | "products"
  | "links"
  | "orders"
  | "commissions"
  | "withdrawals"
  | "campaigns"
  | "profile"
  | "notifications";

export default function AffiliatePortalPage() {
  const params = useParams();
  const subdomain = ((params?.subdomain as string) || "nextlook").trim();

  const apiBase = useMemo(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${subdomain}.localhost:8000`;
      }
    }
    return "";
  }, [subdomain]);

  const tokenStorageKey = `xylo_affiliate_token_${subdomain}`;
  const emailStorageKey = `xylo_affiliate_email_${subdomain}`;

  const [token, setToken] = useState<string>("");
  const [affiliateEmail, setAffiliateEmail] = useState<string>("");
  const [authReady, setAuthReady] = useState(false);

  // Public config & dynamic registration form fields
  const [publicConfig, setPublicConfig] = useState<any>(null);
  const [authTab, setAuthTab] = useState<"login" | "register" | "forgot">("login");
  const [authLoading, setAuthLoading] = useState(false);
  const [authMsg, setAuthMsg] = useState<{ text: string; isError: boolean } | null>(null);

  // Login / Forgot form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotVerifyCode, setForgotVerifyCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");

  // Dynamic Registration form state
  const [regValues, setRegValues] = useState<Record<string, any>>({});
  const [regFiles, setRegFiles] = useState<Record<string, File>>({});

  // Logged-in Portal State
  const [activeTab, setActiveTab] = useState<PortalTab>("overview");
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalData, setPortalData] = useState<any>(null);
  const [copiedText, setCopiedText] = useState<string>("");
  const [toast, setToast] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Link generator form
  const [selectedProdForLink, setSelectedProdForLink] = useState<string>("");
  const [selectedCampForLink, setSelectedCampForLink] = useState<string>("");
  const [linkSourceChannel, setLinkSourceChannel] = useState<string>("facebook");
  const [productSearch, setProductSearch] = useState("");
  const [commFilter, setCommFilter] = useState("all");

  // Withdrawal form
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState("bkash");
  const [withdrawAccount, setWithdrawAccount] = useState("");
  const [withdrawNote, setWithdrawNote] = useState("");
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);

  // Profile form
  const [profileForm, setProfileForm] = useState<Record<string, string>>({});

  const showToast = (text: string, type: "success" | "error" = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleCopy = (text: string, label = "লিংক কপি হয়েছে!") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    showToast(label, "success");
    setTimeout(() => setCopiedText(""), 2500);
  };

  // Load saved token & public config
  const fetchPublicConfig = useCallback(async () => {
    try {
      const res = await fetch(
        `${apiBase}/api/v1/affiliates/public-config/?subdomain=${encodeURIComponent(subdomain)}`,
        {
          headers: {
            Accept: "application/json",
            "X-Tenant-Subdomain": subdomain,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setPublicConfig(data);
      }
    } catch {}
  }, [apiBase, subdomain]);

  useEffect(() => {
    fetchPublicConfig();
    if (typeof window !== "undefined") {
      const savedTok = localStorage.getItem(tokenStorageKey) || "";
      const savedEm = localStorage.getItem(emailStorageKey) || "";
      setToken(savedTok);
      setAffiliateEmail(savedEm);
      setAuthReady(true);
    }
  }, [fetchPublicConfig, tokenStorageKey, emailStorageKey]);

  const fetchPortalData = useCallback(
    async (overrideToken?: string, overrideEmail?: string) => {
      const activeTok = overrideToken !== undefined ? overrideToken : token;
      const activeEm = overrideEmail !== undefined ? overrideEmail : affiliateEmail;
      if (!activeTok && !activeEm) return;

      setPortalLoading(true);
      try {
        const res = await fetch(
          `${apiBase}/api/v1/affiliates/portal/?subdomain=${encodeURIComponent(subdomain)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Tenant-Subdomain": subdomain,
              ...(activeTok ? { Authorization: `Bearer ${activeTok}` } : {}),
              ...(activeEm ? { "X-Affiliate-Email": activeEm } : {}),
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          setPortalData(data);
          if (data?.affiliate) {
            setWithdrawMethod(data.affiliate.primary_payout_method || "bkash");
            setWithdrawAccount(data.affiliate.primary_payout_account || "");
            setProfileForm({
              full_name: data.affiliate.full_name || "",
              phone: data.affiliate.phone || "",
              address: data.affiliate.address || "",
              nid_number: data.affiliate.nid_number || "",
              website_url: data.affiliate.website_url || "",
              facebook_url: data.affiliate.facebook_url || "",
              youtube_url: data.affiliate.youtube_url || "",
              bio: data.affiliate.bio || "",
              primary_payout_method: data.affiliate.primary_payout_method || "bkash",
              primary_payout_account: data.affiliate.primary_payout_account || "",
            });
          }
        } else if (res.status === 401) {
          handleLogout();
        }
      } catch {
      } finally {
        setPortalLoading(false);
      }
    },
    [apiBase, subdomain, token, affiliateEmail]
  );

  useEffect(() => {
    if (authReady && (token || affiliateEmail)) {
      fetchPortalData();
    }
  }, [authReady, token, affiliateEmail, fetchPortalData]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMsg(null);
    try {
      const res = await fetch(`${apiBase}/api/v1/affiliates/auth/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Tenant-Subdomain": subdomain,
        },
        body: JSON.stringify({
          email: loginEmail.trim(),
          password: loginPassword,
        }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem(tokenStorageKey, data.token);
        localStorage.setItem(emailStorageKey, data.affiliate?.email || loginEmail.trim());
        setToken(data.token);
        setAffiliateEmail(data.affiliate?.email || loginEmail.trim());
        await fetchPortalData(data.token, data.affiliate?.email || loginEmail.trim());
      } else {
        setAuthMsg({
          text: data.error || data.detail || "লগইন করতে ব্যর্থ হয়েছে।",
          isError: true,
        });
      }
    } catch {
      setAuthMsg({ text: "সার্ভারের সাথে সংযোগ স্থাপন করা যায়নি।", isError: true });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMsg(null);
    try {
      const formData = new FormData();
      Object.entries(regValues).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          formData.append(k, String(v));
        }
      });
      Object.entries(regFiles).forEach(([k, file]) => {
        if (file) {
          formData.append(k, file);
        }
      });

      const res = await fetch(`${apiBase}/api/v1/affiliates/auth/register/`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Tenant-Subdomain": subdomain,
        },
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem(tokenStorageKey, data.token);
        localStorage.setItem(emailStorageKey, data.affiliate?.email || regValues.email || "");
        setToken(data.token);
        setAffiliateEmail(data.affiliate?.email || regValues.email || "");
        showToast(data.message || "রেজিস্ট্রেশন সফল হয়েছে!", "success");
        await fetchPortalData(data.token, data.affiliate?.email || regValues.email || "");
      } else {
        setAuthMsg({
          text: data.error || data.detail || "রেজিস্ট্রেশন সম্পন্ন করা যায়নি।",
          isError: true,
        });
      }
    } catch {
      setAuthMsg({ text: "সার্ভারের সাথে সংযোগ স্থাপন করা যায়নি।", isError: true });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMsg(null);
    try {
      const res = await fetch(`${apiBase}/api/v1/affiliates/auth/forgot-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Tenant-Subdomain": subdomain,
        },
        body: JSON.stringify({
          email: forgotEmail.trim(),
          phone_or_code: forgotVerifyCode.trim(),
          new_password: forgotNewPassword,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setAuthMsg({ text: data.message || "পাসওয়ার্ড পরিবর্তন হয়েছে!", isError: false });
        setTimeout(() => setAuthTab("login"), 1500);
      } else {
        setAuthMsg({ text: data.error || "পাসওয়ার্ড রিসেট ব্যর্থ হয়েছে।", isError: true });
      }
    } catch {
      setAuthMsg({ text: "সার্ভার ত্রুটি।", isError: true });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(tokenStorageKey);
      localStorage.removeItem(emailStorageKey);
    }
    setToken("");
    setAffiliateEmail("");
    setPortalData(null);
  };

  const executePortalAction = async (payload: Record<string, any>, successMsg?: string) => {
    try {
      const res = await fetch(`${apiBase}/api/v1/affiliates/portal/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Tenant-Subdomain": subdomain,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(affiliateEmail ? { "X-Affiliate-Email": affiliateEmail } : {}),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(successMsg || data.message || "সফলভাবে সম্পন্ন হয়েছে!", "success");
        fetchPortalData();
        return data;
      } else {
        showToast(data.error || data.detail || "সমস্যা হয়েছে।", "error");
        return null;
      }
    } catch {
      showToast("সার্ভারের সাথে সংযোগ বিচ্ছিন্ন।", "error");
      return null;
    }
  };

  const handleSubmitWithdrawal = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingWithdraw(true);
    await executePortalAction({
      action: "request_withdrawal",
      amount: withdrawAmount,
      payment_method: withdrawMethod,
      account_number: withdrawAccount,
      note: withdrawNote,
    });
    setWithdrawAmount("");
    setWithdrawNote("");
    setSubmittingWithdraw(false);
  };

  const storeName = publicConfig?.store?.name || subdomain.toUpperCase();
  const registrationFields: any[] = publicConfig?.registration_fields || [
    { field_key: "full_name", label: "Full Name (পূর্ণ নাম)", field_type: "text", is_required: true },
    { field_key: "email", label: "Email Address (ইমেইল)", field_type: "email", is_required: true },
    { field_key: "phone", label: "Phone Number (মোবাইল নম্বর)", field_type: "phone", is_required: true },
    { field_key: "password", label: "Password (পাসওয়ার্ড)", field_type: "password", is_required: true },
  ];

  // =========================================================================
  // 🌟 VIEW 1: AFFILIATE REGISTRATION & LOGIN SCREEN (NOT AUTHENTICATED)
  // =========================================================================
  if (!token && !portalData) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between p-4 sm:p-8 relative overflow-hidden">
        <div className="max-w-5xl w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center my-auto z-10">
          {/* Left Hero Column */}
          <div className="lg:col-span-5 space-y-6">
            <Link
              href="/"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-xs font-bold text-emerald-400 hover:bg-slate-800 transition-all"
            >
              <Store className="w-3.5 h-3.5" />
              <span>{storeName} স্টোরে ফিরে যান</span>
            </Link>

            <div className="space-y-3">
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[11px] font-black uppercase tracking-wider">
                Official Affiliate Partner Program
              </span>
              <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight">
                {publicConfig?.program?.title || `${storeName} Affiliate Marketing`}
              </h1>
              <p className="text-sm text-slate-400 leading-relaxed">
                {publicConfig?.program?.terms ||
                  "প্রোডাক্টের অ্যাফিলিয়েট লিংক তৈরি করে Facebook, YouTube বা Website-এ শেয়ার করুন। কাস্টমারের অর্ডার সফলভাবে ডেলিভারি হলেই সরাসরি কমিশন আয় করুন!"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800">
                <div className="text-xs text-slate-400 font-semibold">ডিফল্ট কমিশন রেট</div>
                <div className="text-2xl font-black text-emerald-400 mt-1">
                  {publicConfig?.program?.default_commission_rate ?? 10}
                  {publicConfig?.program?.default_commission_type === "fixed" ? " ৳" : "%"}
                </div>
              </div>
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800">
                <div className="text-xs text-slate-400 font-semibold">কুকি ট্র্যাকিং মেয়াদ</div>
                <div className="text-2xl font-black text-amber-400 mt-1">
                  {publicConfig?.program?.cookie_duration_days ?? 30} দিন
                </div>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800/80 space-y-2 text-xs text-slate-300">
              <div className="font-black text-white flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-emerald-400" /> কিভাবে কাজ করে?
              </div>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>অ্যাফিলিয়েট হিসেবে রেজিস্ট্রেশন ও লগইন করুন।</li>
                <li>অ্যাডমিন আপনার অ্যাকাউন্ট অনুমোদন (Approve) করবে।</li>
                <li>যেকোনো প্রোডাক্ট থেকে আপনার ইউনিক রেফারেল লিংক তৈরি ও শেয়ার করুন।</li>
                <li>অর্ডার সফলভাবে Delivered হলে আপনার ওয়ালেটে কমিশন জমা হবে!</li>
              </ol>
            </div>
          </div>

          {/* Right Auth Card */}
          <div className="lg:col-span-7 bg-slate-900/95 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl">
            <div className="flex items-center gap-2 p-1.5 bg-slate-950 rounded-2xl border border-slate-800 mb-6">
              <button
                type="button"
                onClick={() => {
                  setAuthTab("login");
                  setAuthMsg(null);
                }}
                className={`flex-1 py-2.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                  authTab === "login"
                    ? "bg-emerald-600 text-white shadow-lg"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                অ্যাফিলিয়েট লগইন (Login)
              </button>
              <button
                type="button"
                onClick={() => {
                  setAuthTab("register");
                  setAuthMsg(null);
                }}
                className={`flex-1 py-2.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                  authTab === "register"
                    ? "bg-emerald-600 text-white shadow-lg"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                নতুন রেজিস্ট্রেশন (Register)
              </button>
              <button
                type="button"
                onClick={() => {
                  setAuthTab("forgot");
                  setAuthMsg(null);
                }}
                className={`px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  authTab === "forgot"
                    ? "bg-amber-500 text-slate-950 font-black"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                পাসওয়ার্ড রিসেট
              </button>
            </div>

            {authMsg && (
              <div
                className={`mb-5 p-3.5 rounded-2xl border text-xs font-bold flex items-center gap-2 ${
                  authMsg.isError
                    ? "bg-rose-500/10 border-rose-500/40 text-rose-300"
                    : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                }`}
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{authMsg.text}</span>
              </div>
            )}

            {authTab === "login" && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    ইমেইল অ্যাড্রেস (Email) *
                  </label>
                  <input
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="affiliate@example.com"
                    className="w-full px-4 py-3 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    পাসওয়ার্ড (Password) *
                  </label>
                  <input
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/25 cursor-pointer disabled:opacity-50"
                >
                  {authLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <span>ড্যাশবোর্ডে প্রবেশ করুন</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}

            {authTab === "register" && (
              <form onSubmit={handleRegister} className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-[11px] text-slate-400">
                  <span>
                    রেজিস্ট্রেশন ফর্ম মোড:{" "}
                    <strong className="text-emerald-400 uppercase">
                      {publicConfig?.program?.registration_mode || "standard"}
                    </strong>
                  </span>
                  <span>* চিহ্নিত ঘরগুলো আবশ্যক</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {registrationFields.map((field: any) => {
                    const fKey = field.field_key;
                    const fType = field.field_type || "text";
                    const isFullWidth = ["textarea", "file", "address"].includes(fType) || fKey === "address";

                    return (
                      <div key={fKey} className={isFullWidth ? "sm:col-span-2" : ""}>
                        <label className="block text-xs font-bold text-slate-300 mb-1.5">
                          {field.label} {field.is_required && <span className="text-rose-400">*</span>}
                        </label>

                        {fType === "textarea" ? (
                          <textarea
                            rows={2}
                            required={Boolean(field.is_required)}
                            placeholder={field.placeholder || ""}
                            value={regValues[fKey] || ""}
                            onChange={(e) =>
                              setRegValues((prev) => ({ ...prev, [fKey]: e.target.value }))
                            }
                            className="w-full px-3.5 py-2.5 rounded-2xl bg-slate-950 border border-slate-800 text-xs text-white focus:border-emerald-500 focus:outline-none"
                          />
                        ) : fType === "dropdown" ? (
                          <select
                            required={Boolean(field.is_required)}
                            value={regValues[fKey] || ""}
                            onChange={(e) =>
                              setRegValues((prev) => ({ ...prev, [fKey]: e.target.value }))
                            }
                            className="w-full px-3.5 py-2.5 rounded-2xl bg-slate-950 border border-slate-800 text-xs text-white focus:border-emerald-500 focus:outline-none"
                          >
                            <option value="">নির্বাচন করুন</option>
                            {(field.options || []).map((opt: string) => (
                              <option key={opt} value={opt}>
                                {opt.toUpperCase()}
                              </option>
                            ))}
                          </select>
                        ) : fType === "radio" ? (
                          <div className="flex flex-wrap gap-3 pt-1">
                            {(field.options || []).map((opt: string) => (
                              <label key={opt} className="inline-flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
                                <input
                                  type="radio"
                                  name={fKey}
                                  value={opt}
                                  required={Boolean(field.is_required)}
                                  checked={regValues[fKey] === opt}
                                  onChange={() =>
                                    setRegValues((prev) => ({ ...prev, [fKey]: opt }))
                                  }
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                        ) : fType === "checkbox" ? (
                          <label className="inline-flex items-center gap-2 text-xs text-slate-300 pt-1 cursor-pointer">
                            <input
                              type="checkbox"
                              required={Boolean(field.is_required)}
                              checked={Boolean(regValues[fKey])}
                              onChange={(e) =>
                                setRegValues((prev) => ({ ...prev, [fKey]: e.target.checked }))
                              }
                            />
                            <span>{field.placeholder || field.label}</span>
                          </label>
                        ) : fType === "file" ? (
                          <input
                            type="file"
                            required={Boolean(field.is_required)}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) setRegFiles((prev) => ({ ...prev, [fKey]: f }));
                            }}
                            className="w-full px-3 py-2 rounded-2xl bg-slate-950 border border-slate-800 text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:bg-emerald-600 file:text-white file:text-xs file:font-bold"
                          />
                        ) : (
                          <input
                            type={
                              fType === "phone"
                                ? "tel"
                                : ["email", "password", "number", "date", "url"].includes(fType)
                                ? fType
                                : "text"
                            }
                            required={Boolean(field.is_required)}
                            placeholder={field.placeholder || ""}
                            value={regValues[fKey] || ""}
                            onChange={(e) =>
                              setRegValues((prev) => ({ ...prev, [fKey]: e.target.value }))
                            }
                            className="w-full px-3.5 py-2.5 rounded-2xl bg-slate-950 border border-slate-800 text-xs text-white focus:border-emerald-500 focus:outline-none"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/25 cursor-pointer disabled:opacity-50"
                >
                  {authLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <span>অ্যাফিলিয়েট অ্যাকাউন্ট তৈরি করুন</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}

            {authTab === "forgot" && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    আপনার রেজিস্টার্ড ইমেইল *
                  </label>
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="affiliate@example.com"
                    className="w-full px-4 py-3 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    মোবাইল নম্বর অথবা রেফারেল কোড (যাচাইয়ের জন্য)
                  </label>
                  <input
                    type="text"
                    value={forgotVerifyCode}
                    onChange={(e) => setForgotVerifyCode(e.target.value)}
                    placeholder="017XXXXXXXX বা AFF..."
                    className="w-full px-4 py-3 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    নতুন পাসওয়ার্ড (কমপক্ষে ৬ অক্ষর) *
                  </label>
                  <input
                    type="password"
                    required
                    value={forgotNewPassword}
                    onChange={(e) => setForgotNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white"
                  />
                </div>
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm flex items-center justify-center gap-2 cursor-pointer"
                >
                  <KeyRound className="w-4 h-4" />
                  <span>পাসওয়ার্ড পরিবর্তন করুন</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // 🌟 VIEW 2: LOGGED-IN AFFILIATE MARKETER DASHBOARD
  // =========================================================================
  const aff = portalData?.affiliate || {};
  const prog = portalData?.program_settings || {};
  const products: any[] = portalData?.products || [];
  const links: any[] = portalData?.links || [];
  const orders: any[] = portalData?.orders || [];
  const commissions: any[] = portalData?.commissions || [];
  const ledger: any[] = portalData?.ledger || [];
  const withdrawals: any[] = portalData?.withdrawals || [];
  const campaigns: any[] = portalData?.campaigns || [];
  const notifications: any[] = portalData?.notifications || [];

  const filteredProducts = products.filter(
    (p) =>
      !productSearch.trim() ||
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const filteredCommissions = commissions.filter(
    (c) => commFilter === "all" || c.status === commFilter
  );

  const navItems: { id: PortalTab; label: string; icon: any; badge?: number }[] = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "products", label: "Products Marketplace", icon: ShoppingBag, badge: products.length },
    { id: "links", label: "My Referral Links", icon: Link2, badge: links.length },
    { id: "orders", label: "Referred Orders", icon: ClipboardList, badge: orders.length },
    { id: "commissions", label: "Commissions & Ledger", icon: DollarSign },
    { id: "withdrawals", label: "Withdrawals & Payouts", icon: Wallet },
    { id: "campaigns", label: "Campaigns", icon: Megaphone, badge: campaigns.length },
    { id: "profile", label: "Profile & Payment", icon: UserCheck },
    {
      id: "notifications",
      label: "Notifications",
      icon: Bell,
      badge: notifications.filter((n) => !n.is_read).length,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col lg:flex-row">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-2xl border shadow-2xl text-xs font-black flex items-center gap-2 bg-slate-900 border-emerald-500 text-white">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>{toast.text}</span>
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-full lg:w-72 bg-slate-900 border-b lg:border-b-0 lg:border-r border-slate-800 p-4 flex flex-col justify-between shrink-0">
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-2xl bg-emerald-600 flex items-center justify-center font-black text-white">
                {aff.full_name ? aff.full_name.charAt(0).toUpperCase() : "A"}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-black text-white truncate max-w-[140px]">
                    {aff.full_name || aff.email}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[9px] font-black uppercase">
                    {aff.tier || "bronze"}
                  </span>
                </div>
                <div className="text-[10px] font-mono text-emerald-400">
                  ID: {aff.referral_code || "---"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="p-2 rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 text-xs font-bold cursor-pointer"
              title="Log Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-2xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? "bg-emerald-600 text-white font-black shadow-lg shadow-emerald-600/25"
                      : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[9px] font-mono font-black ${
                        isActive ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="hidden lg:block pt-4 border-t border-slate-800 space-y-2">
          <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800">
            <div className="text-[10px] text-slate-400 font-bold uppercase">উত্তোলনযোগ্য ব্যালেন্স</div>
            <div className="text-xl font-black text-emerald-400 mt-0.5">
              ৳{Number(aff.available_balance || 0).toLocaleString()}
            </div>
          </div>
          <Link
            href="/"
            className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold flex items-center justify-center gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>মেইন স্টোর ভিজিট করুন</span>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto space-y-6">
        {/* Approval Status Banner */}
        {aff.status !== "approved" && (
          <div
            className={`p-4 rounded-2xl border flex items-center justify-between gap-4 ${
              aff.status === "pending"
                ? "bg-amber-500/10 border-amber-500/40 text-amber-200"
                : "bg-rose-500/10 border-rose-500/40 text-rose-200"
            }`}
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-400 shrink-0" />
              <div className="text-xs">
                <strong className="font-black block text-sm">
                  {aff.status === "pending"
                    ? "আপনার অ্যাফিলিয়েট অ্যাকাউন্টটি এখন Admin Approval-এর অপেক্ষায় রয়েছে!"
                    : `অ্যাকাউন্ট স্ট্যাটাস: ${String(aff.status || "").toUpperCase()}`}
                </strong>
                <span>
                  {aff.status === "pending"
                    ? "অ্যাডমিন আপনার অ্যাকাউন্ট Approve করার সাথে সাথেই আপনি রেফারেল লিংক তৈরি ও কমিশন আয় শুরু করতে পারবেন।"
                    : aff.rejection_reason || aff.suspension_reason}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Quick Referral Link Bar */}
        <div className="p-4 sm:p-5 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
              <Link2 className="w-4 h-4" /> আপনার মেইন স্টোর রেফারেল লিংক (Unique Referral Link)
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5">
              এই লিংক শেয়ার করলে যেকোনো প্রোডাক্ট অর্ডারে আপনি কমিশন পাবেন (কুকি মেয়াদ:{" "}
              {prog.cookie_duration_days || 30} দিন)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={prog.general_referral_url || ""}
              className="px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs font-mono text-slate-200 w-full sm:w-80"
            />
            <button
              type="button"
              onClick={() => handleCopy(prog.general_referral_url)}
              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              {copiedText === prog.general_referral_url ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              <span>Copy Link</span>
            </button>
          </div>
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: "Total Clicks", value: aff.total_clicks || 0, icon: MousePointerClick, color: "text-sky-400" },
                { label: "Unique Visitors", value: aff.unique_visitors || 0, icon: Users, color: "text-indigo-400" },
                { label: "Referred Orders", value: aff.total_orders || 0, icon: ShoppingBag, color: "text-emerald-400" },
                { label: "Conversion Rate", value: `${aff.conversion_rate || 0}%`, icon: TrendingUp, color: "text-amber-400" },
                { label: "Total Sales", value: `৳${Number(aff.total_sales_amount || 0).toLocaleString()}`, icon: Award, color: "text-purple-400" },
              ].map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.label} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                    <div className="flex items-center justify-between text-xs text-slate-400 font-bold">
                      <span>{card.label}</span>
                      <Icon className={`w-4 h-4 ${card.color}`} />
                    </div>
                    <div className="text-xl font-black text-white">{card.value}</div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 rounded-3xl bg-slate-900 border border-amber-500/30">
                <div className="text-xs font-bold text-amber-400">Pending Commission (ডেলিভারির অপেক্ষায়)</div>
                <div className="text-2xl font-black text-white mt-1">
                  ৳{Number(aff.pending_commission || 0).toLocaleString()}
                </div>
              </div>
              <div className="p-5 rounded-3xl bg-slate-900 border border-emerald-500/40">
                <div className="text-xs font-bold text-emerald-400">Approved Commission (মোট অনুমোদিত)</div>
                <div className="text-2xl font-black text-white mt-1">
                  ৳{Number(aff.approved_commission || 0).toLocaleString()}
                </div>
              </div>
              <div className="p-5 rounded-3xl bg-slate-900 border border-sky-500/40">
                <div className="text-xs font-bold text-sky-400">Paid Commission (মোট উত্তোলনকৃত)</div>
                <div className="text-2xl font-black text-white mt-1">
                  ৳{Number(aff.paid_commission || 0).toLocaleString()}
                </div>
              </div>
              <div className="p-5 rounded-3xl bg-emerald-600/15 border-2 border-emerald-500">
                <div className="text-xs font-black text-emerald-300 uppercase">Available Balance (উত্তোলনযোগ্য)</div>
                <div className="text-2xl font-black text-white mt-1">
                  ৳{Number(aff.available_balance || 0).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Recent Referred Orders */}
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-white">সাম্প্রতিক রেফারেল অর্ডার ও কমিশন</h3>
                <button
                  type="button"
                  onClick={() => setActiveTab("orders")}
                  className="text-xs font-bold text-emerald-400 hover:underline cursor-pointer"
                >
                  সব দেখুন →
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                    <tr>
                      <th className="p-3 rounded-l-xl">Order ID</th>
                      <th className="p-3">Order Date</th>
                      <th className="p-3">Order Status</th>
                      <th className="p-3">Eligible Amount</th>
                      <th className="p-3">Commission</th>
                      <th className="p-3 rounded-r-xl">Commission Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {orders.slice(0, 8).map((o: any) => (
                      <tr key={o.id}>
                        <td className="p-3 font-mono font-bold text-white">#{o.order_number}</td>
                        <td className="p-3 text-slate-400">{o.created_at_formatted}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full bg-slate-800 text-slate-200 text-[10px] font-bold uppercase">
                            {o.order_status || "pending"}
                          </span>
                        </td>
                        <td className="p-3 font-mono">৳{Number(o.eligible_Order_amount || 0).toLocaleString()}</td>
                        <td className="p-3 font-mono font-black text-emerald-400">
                          ৳{Number(o.total_commission || 0).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                              o.status === "approved"
                                ? "bg-emerald-500/20 text-emerald-400"
                                : o.status === "pending"
                                ? "bg-amber-500/20 text-amber-400"
                                : "bg-rose-500/20 text-rose-400"
                            }`}
                          >
                            {o.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-slate-500">
                          এখনো কোনো রেফারেল অর্ডার আসেনি। প্রোডাক্ট লিংক শেয়ার করে আয় শুরু করুন!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: PRODUCTS MARKETPLACE */}
        {activeTab === "products" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900 p-4 rounded-2xl border border-slate-800">
              <div>
                <h2 className="text-base font-black text-white">
                  অ্যাফিলিয়েট প্রোডাক্ট মার্কেটপ্লেস ({filteredProducts.length})
                </h2>
                <p className="text-xs text-slate-400">
                  যেকোনো প্রোডাক্টের রেফারেল লিংক ১-ক্লিকে কপি করে Facebook, YouTube বা Website-এ শেয়ার করুন
                </p>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="প্রোডাক্ট বা SKU খুঁজুন..."
                  className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map((p: any) => (
                <div
                  key={p.id}
                  className="p-4 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col justify-between gap-4 hover:border-emerald-500/50 transition-all"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-black uppercase">
                          {p.assignment_type === "private" ? "🌟 Assigned to You" : p.category_name}
                        </span>
                        <h3 className="text-sm font-black text-white mt-1 line-clamp-2">{p.name}</h3>
                        <p className="text-[11px] font-mono text-slate-400">SKU: {p.sku}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-black text-white">৳{p.price}</div>
                        <div className="text-[10px] font-bold text-emerald-400">
                          কমিশন: {p.commission_rate}
                          {p.commission_type === "percentage" ? "%" : "৳"}
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs">
                      <span className="text-slate-400">প্রতি সেল সম্ভাব্য আয়:</span>
                      <span className="font-mono font-black text-emerald-400 text-sm">
                        +৳{Number(p.estimated_commission || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopy(p.referral_url, `'${p.name}'-এর অ্যাফিলিয়েট লিংক কপি হয়েছে!`)}
                      className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Affiliate Link</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        executePortalAction(
                          { action: "generate_link", product_id: p.id, source_channel: "facebook" },
                          "ট্র্যাকিং লিংক জেনারেট হয়ে My Links ট্যাবে যুক্ত হয়েছে!"
                        )
                      }
                      className="px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold cursor-pointer"
                      title="Save to My Tracking Links"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 3: MY REFERRAL LINKS */}
        {activeTab === "links" && (
          <div className="space-y-6">
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <h3 className="text-sm font-black text-white">নতুন কাস্টম ট্র্যাকিং লিংক তৈরি করুন</h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <select
                  value={selectedProdForLink}
                  onChange={(e) => setSelectedProdForLink(e.target.value)}
                  className="px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                >
                  <option value="">সকল প্রোডাক্ট (Main Storefront)</option>
                  {products.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (৳{p.price})
                    </option>
                  ))}
                </select>

                <select
                  value={selectedCampForLink}
                  onChange={(e) => setSelectedCampForLink(e.target.value)}
                  className="px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                >
                  <option value="">সাধারণ প্রমোশন (No Campaign)</option>
                  {campaigns.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.commission_rate}%)
                    </option>
                  ))}
                </select>

                <select
                  value={linkSourceChannel}
                  onChange={(e) => setLinkSourceChannel(e.target.value)}
                  className="px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                >
                  <option value="facebook">Facebook Page / Group</option>
                  <option value="youtube">YouTube Video Description</option>
                  <option value="website">Blog / Website</option>
                  <option value="whatsapp">WhatsApp / Messenger</option>
                  <option value="tiktok">TikTok / Instagram</option>
                </select>

                <button
                  type="button"
                  onClick={() =>
                    executePortalAction({
                      action: "generate_link",
                      product_id: selectedProdForLink || null,
                      campaign_id: selectedCampForLink || null,
                      source_channel: linkSourceChannel,
                    })
                  }
                  className="py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Generate Link</span>
                </button>
              </div>
            </div>

            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="p-3 rounded-l-xl">Link Code</th>
                    <th className="p-3">Product / Target</th>
                    <th className="p-3">Channel</th>
                    <th className="p-3">Clicks / Unique</th>
                    <th className="p-3">Orders</th>
                    <th className="p-3">Commission Earned</th>
                    <th className="p-3 rounded-r-xl">Copy Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {links.map((lnk: any) => (
                    <tr key={lnk.id}>
                      <td className="p-3 font-mono font-bold text-emerald-400">{lnk.link_code}</td>
                      <td className="p-3 font-bold text-white">
                        {lnk.product_name || lnk.campaign_name || "Main Storefront"}
                      </td>
                      <td className="p-3 uppercase font-mono text-[10px] text-slate-300">
                        {lnk.source_channel}
                      </td>
                      <td className="p-3 font-mono">
                        {lnk.clicks_count} / <span className="text-sky-400">{lnk.unique_clicks_count}</span>
                      </td>
                      <td className="p-3 font-mono font-bold">{lnk.conversions_count}</td>
                      <td className="p-3 font-mono font-black text-emerald-400">
                        ৳{Number(lnk.total_commission_amount || 0).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => handleCopy(lnk.full_referral_url)}
                          className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-emerald-600 text-white text-[11px] font-bold flex items-center gap-1 cursor-pointer"
                        >
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 4: REFERRED ORDERS */}
        {activeTab === "orders" && (
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
            <h2 className="text-base font-black text-white">রেফারেল অর্ডার হিস্ট্রি ({orders.length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="p-3 rounded-l-xl">Order ID</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Order Status</th>
                    <th className="p-3">Subtotal</th>
                    <th className="p-3">Eligible Amount</th>
                    <th className="p-3">Commission</th>
                    <th className="p-3 rounded-r-xl">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {orders.map((o: any) => (
                    <tr key={o.id}>
                      <td className="p-3 font-mono font-bold text-white">#{o.order_number}</td>
                      <td className="p-3 text-slate-400">{o.created_at_formatted}</td>
                      <td className="p-3 uppercase font-bold text-[10px]">{o.order_status}</td>
                      <td className="p-3 font-mono">৳{Number(o.order_subtotal || 0).toLocaleString()}</td>
                      <td className="p-3 font-mono">৳{Number(o.eligible_Order_amount || 0).toLocaleString()}</td>
                      <td className="p-3 font-mono font-black text-emerald-400">
                        ৳{Number(o.total_commission || 0).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                            o.status === "approved"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : o.status === "pending"
                              ? "bg-amber-500/20 text-amber-400"
                              : "bg-rose-500/20 text-rose-400"
                          }`}
                        >
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-6 text-center text-slate-500">
                        কোনো রেফারেল অর্ডার পাওয়া যায়নি।
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 5: COMMISSIONS & FINANCIAL LEDGER */}
        {activeTab === "commissions" && (
          <div className="space-y-6">
            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-black text-white">প্রোডাক্ট ভিত্তিক কমিশন তালিকা</h2>
                <div className="flex items-center gap-1.5">
                  {["all", "pending", "approved", "reversed", "rejected"].map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => setCommFilter(st)}
                      className={`px-3 py-1.5 rounded-xl text-[11px] font-black uppercase cursor-pointer ${
                        commFilter === st
                          ? "bg-emerald-600 text-white"
                          : "bg-slate-950 text-slate-400 hover:text-white"
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                    <tr>
                      <th className="p-3 rounded-l-xl">Order #</th>
                      <th className="p-3">Product</th>
                      <th className="p-3">Eligible Amount</th>
                      <th className="p-3">Rule Applied</th>
                      <th className="p-3">Net Commission</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 rounded-r-xl">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {filteredCommissions.map((c: any) => (
                      <tr key={c.id}>
                        <td className="p-3 font-mono font-bold text-white">#{c.order_number}</td>
                        <td className="p-3 font-bold text-slate-200">
                          {c.product_name} (x{c.quantity})
                        </td>
                        <td className="p-3 font-mono">৳{Number(c.eligible_amount || 0).toLocaleString()}</td>
                        <td className="p-3 text-[11px] text-slate-400">
                          {c.commission_rule_source} ({c.base_commission_rate}
                          {c.commission_type === "percentage" ? "%" : "৳"})
                        </td>
                        <td className="p-3 font-mono font-black text-emerald-400">
                          ৳{Number(c.net_commission_amount || 0).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                              c.status === "approved"
                                ? "bg-emerald-500/20 text-emerald-400"
                                : c.status === "pending"
                                ? "bg-amber-500/20 text-amber-400"
                                : "bg-rose-500/20 text-rose-400"
                            }`}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className="p-3 text-slate-400">{c.created_at_formatted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-3">
              <h3 className="text-sm font-black text-white">Immutable Financial Ledger (ব্যালেন্স অডিট লগ)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                    <tr>
                      <th className="p-3 rounded-l-xl">Type</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Balance Before</th>
                      <th className="p-3">Balance After</th>
                      <th className="p-3">Reason</th>
                      <th className="p-3 rounded-r-xl">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {ledger.map((l: any) => (
                      <tr key={l.id}>
                        <td className="p-3 font-mono text-[11px] font-bold text-slate-200">
                          {l.adjustment_type}
                        </td>
                        <td
                          className={`p-3 font-mono font-black ${
                            Number(l.amount) >= 0 ? "text-emerald-400" : "text-rose-400"
                          }`}
                        >
                          {Number(l.amount) >= 0 ? "+" : ""}৳{Number(l.amount).toLocaleString()}
                        </td>
                        <td className="p-3 font-mono">৳{Number(l.available_balance_before).toLocaleString()}</td>
                        <td className="p-3 font-mono font-bold text-white">
                          ৳{Number(l.available_balance_after).toLocaleString()}
                        </td>
                        <td className="p-3 text-slate-400">{l.reason}</td>
                        <td className="p-3 text-slate-400">{l.created_at_formatted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: WITHDRAWALS & PAYOUTS */}
        {activeTab === "withdrawals" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <h3 className="text-sm font-black text-white">টাকা উত্তোলনের আবেদন (Withdrawal Request)</h3>
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-400">Available Balance</div>
                  <div className="text-2xl font-black text-emerald-400">
                    ৳{Number(aff.available_balance || 0).toLocaleString()}
                  </div>
                </div>
                <div className="text-right text-xs text-slate-400">
                  <div>সর্বনিম্ন: ৳{prog.minimum_withdrawal || 500}</div>
                  <div>প্রসেসিং ফি: {prog.withdrawal_processing_fee_percent || 0}%</div>
                </div>
              </div>

              <form onSubmit={handleSubmitWithdrawal} className="space-y-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">
                    উত্তোলনের পরিমাণ (৳) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder={`সর্বনিম্ন ${prog.minimum_withdrawal || 500}`}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">
                    পেমেন্ট মাধ্যম (bKash / Nagad / Rocket / Bank) *
                  </label>
                  <select
                    value={withdrawMethod}
                    onChange={(e) => setWithdrawMethod(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                  >
                    {(prog.supported_payout_methods || ["bkash", "nagad", "rocket", "bank_transfer"]).map(
                      (m: string) => (
                        <option key={m} value={m}>
                          {m.toUpperCase()}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">
                    অ্যাকাউন্ট / মোবাইল নম্বর *
                  </label>
                  <input
                    type="text"
                    required
                    value={withdrawAccount}
                    onChange={(e) => setWithdrawAccount(e.target.value)}
                    placeholder="017XXXXXXXX বা ব্যাংক অ্যাকাউন্ট নম্বর"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">নোট (ঐচ্ছিক)</label>
                  <input
                    type="text"
                    value={withdrawNote}
                    onChange={(e) => setWithdrawNote(e.target.value)}
                    placeholder="Personal / Agent বা ব্যাংক শাখার নাম"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submittingWithdraw}
                  className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>উইথড্রয়াল রিকোয়েস্ট পাঠান</span>
                </button>
              </form>
            </div>

            <div className="lg:col-span-7 p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <h3 className="text-sm font-black text-white">উইথড্রয়াল ও পেমেন্ট হিস্ট্রি</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                    <tr>
                      <th className="p-3 rounded-l-xl">Ref #</th>
                      <th className="p-3">Amount / Net</th>
                      <th className="p-3">Method & Account</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">TrxID</th>
                      <th className="p-3 rounded-r-xl">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {withdrawals.map((w: any) => (
                      <tr key={w.id}>
                        <td className="p-3 font-mono font-bold text-white">{w.reference_no}</td>
                        <td className="p-3 font-mono">
                          ৳{Number(w.requested_amount).toLocaleString()}
                          <div className="text-[10px] text-emerald-400">
                            Net: ৳{Number(w.net_payable_amount).toLocaleString()}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="uppercase font-bold text-slate-200">{w.payment_method}</span>
                          <div className="text-[11px] font-mono text-slate-400">{w.account_number}</div>
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                              w.status === "paid"
                                ? "bg-emerald-500/20 text-emerald-400"
                                : w.status === "rejected"
                                ? "bg-rose-500/20 text-rose-400"
                                : "bg-amber-500/20 text-amber-400"
                            }`}
                          >
                            {w.status}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-slate-300">{w.transaction_id || "---"}</td>
                        <td className="p-3 text-slate-400">{w.requested_at_formatted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 7: CAMPAIGNS */}
        {activeTab === "campaigns" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campaigns.map((c: any) => (
              <div
                key={c.id}
                className="p-5 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col justify-between gap-4"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase">
                      কমিশন রেট: {c.commission_rate}
                      {c.commission_type === "percentage" ? "%" : "৳"}
                    </span>
                    <span className="text-[11px] text-slate-400">{c.start_date_formatted}</span>
                  </div>
                  <h3 className="text-base font-black text-white">{c.name}</h3>
                  <p className="text-xs text-slate-400">{c.description}</p>
                </div>

                <div className="flex items-center gap-2">
                  {c.my_application_status === "approved" ? (
                    <button
                      type="button"
                      onClick={() => handleCopy(c.referral_url, "ক্যাম্পেইন রেফারেল লিংক কপি হয়েছে!")}
                      className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Campaign Link</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => executePortalAction({ action: "apply_campaign", campaign_id: c.id })}
                      className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black cursor-pointer"
                    >
                      {c.my_application_status === "pending"
                        ? "আবেদন অপেক্ষমাণ (Pending)"
                        : "ক্যাম্পেইনে জয়েন করুন"}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {campaigns.length === 0 && (
              <div className="col-span-full p-8 text-center text-slate-500 bg-slate-900 rounded-3xl border border-slate-800 text-xs">
                বর্তমানে কোনো স্পেশাল ক্যাম্পেইন চলমান নেই।
              </div>
            )}
          </div>
        )}

        {/* TAB 8: PROFILE & PAYMENT DETAILS */}
        {activeTab === "profile" && (
          <div className="max-w-2xl p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
            <h2 className="text-base font-black text-white">প্রোফাইল ও পেমেন্ট সেটিংস</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: "full_name", label: "পূর্ণ নাম (Full Name)" },
                { key: "phone", label: "মোবাইল নম্বর (Phone)" },
                { key: "nid_number", label: "NID নম্বর" },
                { key: "primary_payout_account", label: "ডিফল্ট পেমেন্ট অ্যাকাউন্ট নম্বর" },
                { key: "facebook_url", label: "Facebook লিংক" },
                { key: "youtube_url", label: "YouTube / Website লিংক" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-bold text-slate-300 mb-1">{f.label}</label>
                  <input
                    type="text"
                    value={profileForm[f.key] || ""}
                    onChange={(e) =>
                      setProfileForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                    }
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-300 mb-1">ঠিকানা (Address)</label>
                <textarea
                  rows={2}
                  value={profileForm.address || ""}
                  onChange={(e) => setProfileForm((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => executePortalAction({ action: "update_profile", ...profileForm })}
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black cursor-pointer"
            >
              প্রোফাইল সেভ করুন
            </button>
          </div>
        )}

        {/* TAB 9: NOTIFICATIONS */}
        {activeTab === "notifications" && (
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-black text-white">নোটিফিকেশন ও আপডেট</h2>
              <button
                type="button"
                onClick={() => executePortalAction({ action: "mark_notifications_read" })}
                className="text-xs font-bold text-emerald-400 hover:underline cursor-pointer"
              >
                সব পঠিত হিসেবে চিহ্নিত করুন
              </button>
            </div>
            <div className="space-y-2.5">
              {notifications.map((n: any) => (
                <div
                  key={n.id}
                  className={`p-4 rounded-2xl border ${
                    n.is_read
                      ? "bg-slate-950/60 border-slate-800"
                      : "bg-emerald-500/5 border-emerald-500/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-black text-white">{n.title}</h4>
                    <span className="text-[10px] font-mono text-slate-400">{n.created_at_formatted}</span>
                  </div>
                  <p className="text-xs text-slate-300 mt-1">{n.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
